import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Max digit count from `maxlength` (HTML does not reliably enforce this on `type="number"`).
   * @returns {number | null}
   */
  #getMaxDigits() {
    const input = this.refs.quantityInput;
    const raw = input.getAttribute('maxlength');
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * `type="number"` inputs do not expose selectionStart/selectionEnd in most
   * browsers. When the field is already at max length, treat the next digit as
   * replacing the highlighted value (e.g. after focus selects all).
   * @param {HTMLInputElement} input
   * @param {number | null} max
   */
  #resolveDigitInsertRange(input, max) {
    const value = String(input.value ?? '');
    const digitCount = value.replace(/\D/g, '').length;
    const rawStart = input.selectionStart;
    const rawEnd = input.selectionEnd;
    if (rawStart != null && rawEnd != null) {
      return { start: rawStart, end: rawEnd, value };
    }
    if (max != null && digitCount >= max) {
      return { start: 0, end: value.length, value };
    }
    return { start: value.length, end: value.length, value };
  }

  #enforceMaxDigits() {
    const input = this.refs.quantityInput;
    const max = this.#getMaxDigits();
    if (max == null) return;
    let cleaned = String(input.value).replace(/\D/g, '');
    if (cleaned.length <= max) return;
    cleaned = cleaned.slice(0, max);
    input.value = cleaned;
    try {
      input.setSelectionRange(cleaned.length, cleaned.length);
    } catch (_) {}
  }

  /** @type {(e: KeyboardEvent) => void} */
  #onDigitsOnlyKeydown = (e) => {
    if (e.defaultPrevented) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === '-' || e.code === 'Minus' || e.code === 'NumpadSubtract') {
      e.preventDefault();
      return;
    }
    const allowedNav = new Set([
      'Tab',
      'Escape',
      'Enter',
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]);
    if (allowedNav.has(e.key)) return;
    if (e.key.length === 1 && /[0-9]/.test(e.key)) {
      const input = this.refs.quantityInput;
      const max = this.#getMaxDigits();
      if (max != null) {
        const { start, end, value } = this.#resolveDigitInsertRange(input, max);
        const merged = (value.slice(0, start) + e.key + value.slice(end)).replace(/\D/g, '');
        if (merged.length > max) {
          e.preventDefault();
          return;
        }
      }
      return;
    }
    e.preventDefault();
  };

  /** @type {(e: Event) => void} */
  #onDigitsOnlyBeforeInput = (e) => {
    if (!(e instanceof InputEvent)) return;
    if (e.inputType === 'insertFromPaste') return;
    if (e.inputType?.startsWith('delete')) return;
    const data = e.data;
    if (data == null || data === '') return;
    if (!/^[0-9]+$/.test(data)) {
      e.preventDefault();
      return;
    }
    const input = this.refs.quantityInput;
    const max = this.#getMaxDigits();
    if (max == null) return;
    const { start, end, value } = this.#resolveDigitInsertRange(input, max);
    const merged = (value.slice(0, start) + data + value.slice(end)).replace(/\D/g, '');
    if (merged.length > max) {
      e.preventDefault();
    }
  };

  /** @type {(e: ClipboardEvent) => void} */
  #onDigitsOnlyPaste = (e) => {
    const input = this.refs.quantityInput;
    const max = this.#getMaxDigits();
    const text = e.clipboardData?.getData('text') ?? '';
    const digits = text.replace(/\D/g, '');
    if (!digits) {
      e.preventDefault();
      return;
    }

    const { start, end, value } = this.#resolveDigitInsertRange(input, max);
    const merged = (value.slice(0, start) + digits + value.slice(end)).replace(/\D/g, '');
    const next = max != null ? merged.slice(0, max) : merged;

    if (text === digits && merged === next) {
      return;
    }

    e.preventDefault();
    input.value = next;
    const caret = merged.length > next.length ? next.length : Math.min(start + digits.length, next.length);
    try {
      input.setSelectionRange(caret, caret);
    } catch (_) {}
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /** @type {() => void} */
  #onDigitsOnlyInput = () => {
    const input = this.refs.quantityInput;
    const max = this.#getMaxDigits();
    let cleaned = input.value.replace(/\D/g, '');
    if (max != null && cleaned.length > max) cleaned = cleaned.slice(0, max);
    if (input.value === cleaned) return;
    input.value = cleaned;
    const len = cleaned.length;
    try {
      input.setSelectionRange(len, len);
    } catch (_) {}
  };

  connectedCallback() {
    super.connectedCallback();
    queueMicrotask(() => this.#bindDigitsOnlyGuards());
  }

  updatedCallback() {
    super.updatedCallback();
    queueMicrotask(() => this.#bindDigitsOnlyGuards());
  }

  disconnectedCallback() {
    this.#unbindDigitsOnlyGuards();
    super.disconnectedCallback();
  }

  #bindDigitsOnlyGuards() {
    const input = this.refs.quantityInput;
    if (!input || input.dataset.rsQtyDigitsOnly === '1') return;
    input.dataset.rsQtyDigitsOnly = '1';
    input.addEventListener('keydown', this.#onDigitsOnlyKeydown);
    input.addEventListener('beforeinput', this.#onDigitsOnlyBeforeInput);
    input.addEventListener('paste', this.#onDigitsOnlyPaste);
    input.addEventListener('input', this.#onDigitsOnlyInput);
  }

  #unbindDigitsOnlyGuards() {
    const input = this.refs.quantityInput;
    if (!input || input.dataset.rsQtyDigitsOnly !== '1') return;
    delete input.dataset.rsQtyDigitsOnly;
    input.removeEventListener('keydown', this.#onDigitsOnlyKeydown);
    input.removeEventListener('beforeinput', this.#onDigitsOnlyBeforeInput);
    input.removeEventListener('paste', this.#onDigitsOnlyPaste);
    input.removeEventListener('input', this.#onDigitsOnlyInput);
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#enforceMaxDigits();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#enforceMaxDigits();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#enforceMaxDigits();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    const min = parseInt(quantityInput.min) || 1;
    const newValue = parseInt(quantityInput.value);

    const cell = this.closest('td');
    const errorMsg = /** @type {HTMLElement | null} */ (cell?.querySelector('.rs-error__message'));
    const errorSpan = /** @type {HTMLElement | null} */ (cell?.querySelector('.rs-error__message-text--min-quantity'));

    if (isNaN(newValue) || quantityInput.value.trim() === '') {
      // Reset to min order qty instead of showing an error when field is cleared
      quantityInput.value = String(min);
      if (errorMsg) errorMsg.style.display = 'none';
    } else if (newValue < min) {
      if (errorMsg) errorMsg.style.display = 'flex';
      if (errorSpan) errorSpan.textContent = /** @type {any} */ (window).__t?.('minimum_order', { count: min }) ?? errorSpan.textContent;
      return;
    }

    if (errorMsg) errorMsg.style.display = 'none';

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
